import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import cellTypeMap from '../cells/cellTypeMap';
import macroTypeMap from '../macros/macroTypeMap';
import { accessor } from 'src/store';
import { PreviousMacroItemData, PreviousScreenItemData } from '../getAppletDesignChanges';
import { ChangeLog } from 'src/components/EntityDetail';
import type { PendingMediaObjectReference } from '@redviking/argonaut-util/src/media.util';
import { getCellName as getCellNameUtil } from '@redviking/argonaut-util/src/mes/appletUtil';

export function getAppletDesignPendingMedia (): PendingMediaObjectReference[] {
    // Get the pending media from the applet design version entity
    // we do this by scanning through screens and cells because we only
    // want to upload what is referenced in the design
    const pendingMedia: PendingMediaObjectReference[] = [];
    const entity = accessor.entityAsType('appletDesignVersion');
    if (!entity) {
        return pendingMedia;
    }
    for (const screen of entity.config.screens) {
        for (const cell of screen.cells) {
            if (cell.type === 'media') {
                cell.attrs.items.forEach(item => {
                    if (item.media.type === 'pending') {
                        if (!pendingMedia.some(pm => item.media.type === 'pending' && pm.id === item.media.id!)) {
                            pendingMedia.push({
                                type: 'pending',
                                id: item.media.id,
                            });
                        }
                    }
                });
            }
        }
    }
    return pendingMedia;
}

export const getScreenPreviousItemData = (originalEntity: Latest.AppletDesignVersionConfig, originalScreen: Latest.Screen.Config): PreviousScreenItemData => {
    const macros: Latest.Macros.AppletMacro[] = [];

    for (const macroCell of originalScreen.cells.filter((c): c is Latest.Screen.Cells.MacroTargetCell => c.type === 'macroTarget')) {
        const macro = originalEntity.macros.find(m => m.id === macroCell.attrs.macroId);
        if (macro) {
            macros.push(macro);
        }
    }
    return {
        macros,
        screen: originalScreen,
        scripts: originalEntity.scripts.filter(s => originalScreen.cells.find(c => c.type === 'btn' && c.attrs.linkedScriptId === s.id)) || [],
    };
};

export const getCellType = (cell: Latest.Screen.Cells.AppletCell): string => {
    if (cell.type === 'macroTarget') {
        return macroTypeMap[cell.attrs.macroType].text;
    }
    return cellTypeMap[cell.type].text;
};

export const getCellName = (cell: Latest.Screen.Cells.AppletCell): string => {
    let distinguishableIdentifierResult = null;
    const maybeDistinguishableIdentifier = cellTypeMap[cell.type].distinguishableIdentifier;
    if (maybeDistinguishableIdentifier) {
        distinguishableIdentifierResult = maybeDistinguishableIdentifier(cell);
    }

    return getCellNameUtil({
        cell,
        defaultText: cellTypeMap[cell.type].text,
        designConfig: accessor.entityAsType('appletDesignVersion')!.config,
        distinguishableIdentifier: distinguishableIdentifierResult as keyof typeof cell['attrs'],
    });
};

export const getScreenUpdateMessages = (payload: {
    newScreen: Latest.Screen.Config;
    originalScreen?: Latest.Screen.Config;
}): ChangeLog.MessageType[] => {
    const ret: ChangeLog.MessageType[] = [];

    for (const newCell of payload.newScreen.cells) {
        const originalCell = payload.originalScreen?.cells.find(c => c.id === newCell.id);
        if (originalCell) {
            // TODO: Add in more detailed messages for cell updates based on cell type
            // NOTE: Not in scope at the moment
        } else {
            ret.push({
                type: 'interpolate',
                text: 'A {cellType} cell named {cellName} was added',
                data: [
                    {
                        type: 'code',
                        name: 'cellName',
                        value: getCellName(newCell),
                    },
                    {
                        type: 'code',
                        name: 'cellType',
                        value: getCellType(newCell),
                    },
                ],
            });
        }
    }

    for (const oldCell of payload.originalScreen?.cells || []) {
        if (!payload.newScreen.cells.find(c => c.id === oldCell.id)) {
            ret.push({
                type: 'interpolate',
                text: 'A {cellType} cell named {cellName} was removed',
                data: [
                    {
                        type: 'code',
                        name: 'cellName',
                        value: getCellName(oldCell),
                    },
                    {
                        type: 'code',
                        name: 'cellType',
                        value: getCellType(oldCell),
                    },
                ],
            });
        }
    }

    return ret;
};

export const getMacroPreviousItemData = (originalEntity: Latest.AppletDesignVersionConfig, originalMacro: Latest.Macros.AppletMacro): PreviousMacroItemData => {
    const cellFromCfg = accessor.appletDesign.findCellInCfg(c => c.type === 'macroTarget' && c.attrs.macroId === originalMacro.id, true);
    if (!cellFromCfg) {
        throw new Error(`Could not find cell for macro ${originalMacro.id}`);
    }

    const childCells: {
        cellIdx: number;
        cellCfg: Latest.Screen.Cells.AppletCell;
    }[] = accessor.appletDesign.getAllChildCells(cellFromCfg.cellCfg.id, true).map(cell => ({
        cellIdx: cell.cellIdx,
        cellCfg: cell.cellCfg,
    }));

    return {
        macroCfg: originalMacro,
        cellData: {
            screenId: cellFromCfg.screen.id,
            screenIdx: cellFromCfg.screenIdx,
            cellCfgs: [
                {
                    cellCfg: cellFromCfg.cellCfg,
                    cellIdx: cellFromCfg.cellIdx,
                },
                ...childCells,
            ],
        },
    };
};
